import { useAuthStore } from '@/stores/auth';
import { store, key } from '@/storeVuex';
import { addSentryBreadcrumb, captureApiError } from '@/plugins/monitoring';
import { observeFromResponse } from '@/utils/permissionsVersion';
import { redirectToLogin } from '@/utils/sessionRedirect';

export const fetchWrapper = {
    get: request('GET'),
    post: request('POST'),
    put: request('PUT'),
    patch: request('PATCH'),
    delete: request('DELETE')
};

/**
 * The third argument exists because callers were already passing one.
 *
 * `$axios` is THIS wrapper, not axios, and `request()` took (url, body) — so
 * `this.$axios.post(url, {}, { signal })` dropped the signal on the floor
 * without a word. Two call sites in TradeChecklist built an AbortController,
 * armed a 10-second timeout and aborted a controller that nothing was listening
 * to: the "timeout protection" their comments describe did not exist, and the
 * request could hang for as long as the network let it.
 *
 * Only `signal` is honoured. Accepting a whole RequestInit here would let a
 * caller quietly override the method, the auth header or the body that the rest
 * of this function is responsible for.
 */
function request(method: string) {
    return (url: any, body?: any, options?: { signal?: AbortSignal }) => {
        // Use the URL directly without proxy conversion
        let requestUrl = url;
        // Commented out proxy conversion - use staging API directly
        // if (import.meta.env.DEV && url.includes('stagingapi.builtsimple.com.au/api')) {
        //     // Replace absolute URL with relative URL to use Vite proxy
        //     requestUrl = url.replace('https://stagingapi.builtsimple.com.au', '');
        // }

        const requestOptions: any = {
            method,
            headers: authHeader(url) // Keep original URL for auth header check
        };
        // GET/HEAD requests cannot legally carry a body — fetch throws
        // "Request with GET/HEAD method cannot have body". If a caller passes one
        // by mistake, ignore it (GET data belongs in the query string) rather than
        // crashing. Attach the body only for methods that allow one.
        if (body && method !== 'GET' && method !== 'HEAD') {
            // Check if body is FormData (for file uploads)
            if (body instanceof FormData) {
                // Don't set Content-Type for FormData - browser will set it with boundary
                requestOptions.body = body;
            } else {
                requestOptions.headers['Content-Type'] = 'application/json';
                requestOptions.body = JSON.stringify(body);
            }
        }
        if (options?.signal) {
            requestOptions.signal = options.signal;
        }
        addSentryBreadcrumb('api', `${method} ${requestUrl}`, {
            url: requestUrl,
            method,
            hasBody: !!body,
        });
        return fetch(requestUrl, requestOptions).then((response) => handleResponse(response, method, requestUrl, body));
    };
}

// helper functions

function authHeader(url: any) {
    const user: any = store.state.user;
    // return auth header with jwt if user is logged in and request is to the api url
    // const { user } = useAuthStore();
    const isLoggedIn = !!user?.token;
    // Check if it's an API URL (either absolute or relative path starting with /api)
    const gatewayUrl = (import.meta as any).env?.VITE_API_GATEWAY_URL || '';
    const isApiUrl = url.startsWith(import.meta.env.VITE_API_URL)
        || url.startsWith('/api')
        || url.includes('builtsimple.com.au')
        // Built Simple gateway — same Bearer token works there because the
        // gateway validates it against bsapi via /api/userinfo.
        || (!!gatewayUrl && url.startsWith(gatewayUrl))
        || url.includes('localhost:3100')
        || url.includes('127.0.0.1:3100');

    const headers: any = {
        'X-Requested-With': 'XMLHttpRequest' // Required by the API
    };

    if (isLoggedIn && isApiUrl) {
        headers['Authorization'] = `Bearer ${user.token}`;
    }

    return headers;
}

function handleResponse(response: any, method?: string, url?: string, requestBody?: any) {
    // Every API response carries the version of the permission map it was
    // served under; a change means this user's role was edited, so the cached
    // map is re-fetched in the background. See utils/permissionsVersion.
    observeFromResponse(response);

    return response.text().then((text: any) => {
        const data = text && JSON.parse(text);

        if (!response.ok) {
            // Report API error to Sentry with full context
            captureApiError(
                url || response.url || 'unknown',
                method || 'UNKNOWN',
                response.status,
                data,
                requestBody instanceof FormData ? '(FormData)' : requestBody
            );

            // Only a genuine authentication failure should end the session.
            // 401 = not authenticated (token missing/invalid/expired) -> log out.
            // 403 = authenticated but NOT permitted (a Gate/role/permission denial,
            // e.g. a non-admin hitting an admin-only route like /companysettings).
            // Treating 403 as "session expired" wrongly wiped the user's session and
            // bounced them to /login. Keep 403 out of the logout path; only fall back
            // to logout if the body explicitly says "Unauthenticated".
            const isUnauthenticated =
                response.status === 401 ||
                (data && (
                    data.message?.includes('Unauthenticated') ||
                    data.error?.includes('Unauthenticated') ||
                    JSON.stringify(data).includes('Unauthenticated')
                ));

            // Only the bsapi (Laravel) session can be "expired" — 401s from the
            // gateway, AI services, or any other origin must not nuke the user's
            // localStorage/sanctum session. Each non-bsapi caller has its own
            // error UI; we let those bubble up untouched.
            const checkUrl: string = url || response.url || '';
            const gatewayUrl: string = (import.meta as any).env?.VITE_API_GATEWAY_URL || '';
            const isGatewayCall =
                (!!gatewayUrl && checkUrl.includes(gatewayUrl)) ||
                checkUrl.includes('localhost:3100') ||
                checkUrl.includes('127.0.0.1:3100') ||
                checkUrl.includes('gateway.builtsimple.com.au');

            if (isUnauthenticated && !isGatewayCall) {
                addSentryBreadcrumb('auth', 'Session expired - redirecting to login', {
                    status: response.status,
                    url: url || response.url,
                }, 'warning');

                // Clears storage and navigates AT MOST ONCE per document —
                // a page with a dozen calls in flight produces a dozen 401s.
                redirectToLogin();
            }

            // const error = (data && data.message) || response.statusText;
            return Promise.reject(data);
        }

        return data;
    });
}
